/* ================================================================
   HYDRAVA — hv-pages.js
   Behaviour for the content page sections (hv-page-hero,
   hv-story-chapters, hv-cta-band, hv-faq, hv-contact, hv-collection,
   hv-search). Loaded deferred on every page after hv-motion.js.

   - Every section root carries data-hvp="<module>" and is initialised
     on its own, so several can live on one page.
   - Theme Editor: re-inits on shopify:section:load, tears down on
     shopify:section:unload, opens a FAQ when its block is selected.
   - prefers-reduced-motion: no word split, no reveals, no fades.
   - Content never depends on this file: with JS off, FAQ answers are
     open, the contact form posts normally, chips / sort / pagination
     are plain links and GET forms.
   ================================================================ */
(function () {
  'use strict';
  if (window.HVP && window.HVP.loaded) return;

  var d = document;
  var RMQ = window.matchMedia ? matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var reduced = function () { return RMQ.matches; };
  var $ = function (s, r) { return (r || d).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || d).querySelectorAll(s)); };
  var hasIO = 'IntersectionObserver' in window;

  function bag() {
    var fns = [];
    return {
      on: function (t, type, fn, o) { t.addEventListener(type, fn, o); fns.push(function () { t.removeEventListener(type, fn, o); }); },
      add: function (fn) { fns.push(fn); },
      run: function () { fns.splice(0).forEach(function (fn) { try { fn(); } catch (e) { /* noop */ } }); }
    };
  }

  /* ── Word split (same markup + classes as hv-motion.css .hv-split) ── */
  function splitWords(el) {
    if (el.dataset.hvSplitDone) return false;
    var text = el.textContent.trim();
    if (!text) return false;
    el.dataset.hvSplitDone = '1';
    el.setAttribute('aria-label', text);
    el.textContent = '';
    text.split(/\s+/).forEach(function (w, i) {
      var outer = d.createElement('span');
      outer.className = 'hv-w';
      outer.setAttribute('aria-hidden', 'true');
      var inner = d.createElement('span');
      inner.style.setProperty('--i', i);
      inner.textContent = w;
      outer.appendChild(inner);
      el.appendChild(outer);
      el.appendChild(d.createTextNode(' '));
    });
    el.classList.add('hv-split');
    return true;
  }

  /* ── Reveals: only elements below the fold start hidden ── */
  var revealIO = hasIO ? new IntersectionObserver(function (es) {
    es.forEach(function (e) {
      if (e.isIntersecting) { e.target.classList.add('in'); revealIO.unobserve(e.target); }
    });
  }, { rootMargin: '0px 0px -12% 0px' }) : null;

  function setupReveals(root) {
    if (reduced() || !revealIO) return;
    var vh = window.innerHeight;
    $$('[data-hvp-rv],[data-hvp-split]', root).forEach(function (el) {
      if (el.closest('[data-hvp="hero"]')) return; // hero handles its own heading
      if (el.classList.contains('in') || el.classList.contains('hv-rv-wait') || el.classList.contains('hvp-rv3d')) return;
      if (el.getBoundingClientRect().top < vh * 0.9) return; // already on screen: leave it alone
      if (el.hasAttribute('data-hvp-split')) splitWords(el);
      else el.classList.add(el.getAttribute('data-hvp-rv') === '3d' ? 'hvp-rv3d' : 'hv-rv-wait');
      revealIO.observe(el);
    });
  }
  function forgetReveals(root) {
    if (!revealIO) return;
    $$('[data-hvp-rv],[data-hvp-split]', root).forEach(function (el) { revealIO.unobserve(el); });
  }

  var modules = {};

  /* ================================================================
     PAGE HERO: heading reveals word by word on load
     ================================================================ */
  modules.hero = function (root) {
    var h = $('[data-hvp-split]', root);
    if (!h || reduced()) return {};
    if (splitWords(h)) {
      requestAnimationFrame(function () { requestAnimationFrame(function () { h.classList.add('in'); }); });
    }
    return {};
  };

  /* Story chapters + CTA band: reveals only */
  modules.reveal = function () { return {}; };

  /* ================================================================
     FAQ: accordion, live search, category chips
     ================================================================ */
  modules.faq = function (root) {
    var b = bag();
    var items = $$('.hvp-fq', root);
    var search = $('[data-hvp-faq-search]', root);
    var chips = $('[data-hvp-faq-chips]', root);
    var none = $('[data-hvp-faq-none]', root);
    var cat = 'all';

    function setOpen(it, open) {
      it.classList.toggle('is-open', open);
      var q = $('.hvp-fq__q', it);
      if (q) q.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    // sync initial state (open-by-default blocks already carry .is-open)
    items.forEach(function (it) { setOpen(it, it.classList.contains('is-open')); });

    b.on(root, 'click', function (e) {
      var q = e.target.closest('.hvp-fq__q');
      if (q && root.contains(q)) { var it = q.closest('.hvp-fq'); setOpen(it, !it.classList.contains('is-open')); return; }
      var c = e.target.closest('[data-cat]');
      if (c && chips && chips.contains(c)) { cat = c.getAttribute('data-cat'); filter(); }
    });

    function filter() {
      var t = search ? search.value.trim().toLowerCase() : '';
      var n = 0;
      items.forEach(function (it) {
        var ok = (cat === 'all' || it.getAttribute('data-cat') === cat) && (!t || it.textContent.toLowerCase().indexOf(t) > -1);
        it.hidden = !ok;
        if (ok) n++;
      });
      if (none) none.hidden = n > 0;
      if (chips) $$('[data-cat]', chips).forEach(function (c) {
        var on = c.getAttribute('data-cat') === cat;
        c.classList.toggle('is-on', on);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    if (search) { search.hidden = false; b.on(search, 'input', filter); }
    if (chips) chips.hidden = false;

    return {
      destroy: b.run,
      select: function (el) { var it = el.classList.contains('hvp-fq') ? el : el.closest('.hvp-fq'); if (it) { it.hidden = false; setOpen(it, true); } }
    };
  };

  /* ================================================================
     CONTACT: inline validation, focus on result, copy email
     ================================================================ */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  modules.contact = function (root) {
    var b = bag();
    var form = $('form', root);
    var live = $('[data-hvp-live]', root);

    // After a post: bring the result into view and move focus to it.
    var result = $('[data-hvp-cform-ok]', root) || $('[data-hvp-cform-alert]', root);
    if (result && !(window.Shopify && window.Shopify.designMode)) {
      setTimeout(function () {
        result.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' });
        try { result.focus({ preventScroll: true }); } catch (e) { result.focus(); }
      }, 60);
    }

    function setErr(field, msg) {
      var err = d.getElementById(field.getAttribute('aria-describedby'));
      field.classList.toggle('is-bad', !!msg);
      if (msg) field.setAttribute('aria-invalid', 'true'); else field.removeAttribute('aria-invalid');
      if (err) err.textContent = msg || '';
    }
    function check(field) {
      var v = field.value.trim();
      if (field.type === 'email') return EMAIL_RE.test(v) ? '' : field.getAttribute('data-hvp-msg');
      return v ? '' : field.getAttribute('data-hvp-msg');
    }

    if (form) {
      var fields = $$('[data-hvp-msg]', form);
      b.on(form, 'submit', function (e) {
        var first = null;
        fields.forEach(function (f) { var m = check(f); setErr(f, m); if (m && !first) first = f; });
        if (first) { e.preventDefault(); first.focus(); return; }
        var btn = $('button[type="submit"]', form);
        if (btn) btn.setAttribute('aria-busy', 'true');
      });
      fields.forEach(function (f) {
        b.on(f, 'input', function () { if (f.classList.contains('is-bad') && !check(f)) setErr(f, ''); });
        b.on(f, 'blur', function () { if (f.value.trim() && check(f)) setErr(f, check(f)); });
      });
    }

    $$('[data-hvp-copy]', root).forEach(function (btn) {
      btn.hidden = false;
      var t = null;
      b.on(btn, 'click', function () {
        var email = btn.getAttribute('data-hvp-copy');
        function done(msg) {
          btn.textContent = msg;
          if (live) live.textContent = msg;
          clearTimeout(t);
          t = setTimeout(function () { btn.textContent = btn.getAttribute('data-label'); }, 2000);
        }
        function fallback() {
          var link = $('[data-hvp-mail]', btn.parentNode);
          if (link) {
            var r = d.createRange(); r.selectNodeContents(link);
            var sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(r);
          }
          done(btn.getAttribute('data-fallback'));
        }
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(email).then(function () { done(btn.getAttribute('data-done')); }, fallback);
        } else fallback();
      });
      b.add(function () { clearTimeout(t); });
    });

    return { destroy: b.run };
  };

  /* ================================================================
     COLLECTION: chips / sort / pagination via Section Rendering API
     ================================================================ */
  modules.collection = function (root) {
    var b = bag();
    var sid = root.getAttribute('data-section-id');
    var ctrl = null;
    if (!sid || !window.fetch || !window.history || !history.pushState) return {};

    function samePage(url) { return url.origin === location.origin && url.pathname === location.pathname; }

    function load(href, push, scroll) {
      var url = new URL(href, location.href);
      var body = $('[data-hvp-col-body]', root);
      var results = $('[data-hvp-col-results]', root);
      if (!body) { location.href = url.href; return; }
      if (ctrl) ctrl.abort();
      ctrl = 'AbortController' in window ? new AbortController() : null;

      var focusedId = d.activeElement && root.contains(d.activeElement) ? d.activeElement.id : '';
      var fetchUrl = new URL(url.href);
      fetchUrl.searchParams.set('section_id', sid);

      var fadeDone = new Promise(function (res) {
        if (!results || reduced()) return res();
        results.classList.add('is-out', 'is-loading');
        setTimeout(res, 280);
      });
      var got = fetch(fetchUrl.href, { credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); });

      Promise.all([got, fadeDone]).then(function (out) {
        var doc = new DOMParser().parseFromString(out[0], 'text/html');
        var fresh = doc.querySelector('[data-hvp-col-body]');
        if (!fresh) throw new Error('no body');
        body.innerHTML = fresh.innerHTML;
        if (push) history.pushState({ hvpCol: sid }, '', url.href);
        var nr = $('[data-hvp-col-results]', root);
        if (nr && !reduced()) {
          nr.classList.add('is-out');
          requestAnimationFrame(function () { requestAnimationFrame(function () { nr.classList.remove('is-out'); }); });
        }
        if (focusedId) { var f = d.getElementById(focusedId); if (f) f.focus({ preventScroll: true }); }
        if (scroll) {
          var top = root.getBoundingClientRect().top + window.scrollY - 90;
          if (window.scrollY > top) window.scrollTo({ top: top, behavior: reduced() ? 'auto' : 'smooth' });
        }
        d.dispatchEvent(new CustomEvent('hvp:collection:updated', { detail: { sectionId: sid, url: url.href } }));
      }).catch(function (err) {
        if (err && err.name === 'AbortError') return;
        location.href = url.href; // fall back to a normal page load
      });
    }

    b.on(root, 'click', function (e) {
      var a = e.target.closest('a[data-hvp-col-link]');
      if (!a || !root.contains(a)) return;
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var url = new URL(a.href, location.href);
      if (!samePage(url)) return; // another collection: let the browser navigate (hero changes too)
      e.preventDefault();
      load(url.href, true, !!a.closest('.hvp-pager'));
    });

    function sortUrl(form) {
      var url = new URL(form.action, location.href);
      var params = new URLSearchParams();
      Array.prototype.forEach.call(form.elements, function (el) {
        if (el.name && !el.disabled && el.type !== 'submit') params.append(el.name, el.value);
      });
      url.search = params.toString();
      return url;
    }
    b.on(root, 'change', function (e) {
      var sel = e.target;
      if (!sel.matches || !sel.matches('[data-hvp-col-sort] select')) return;
      var url = sortUrl(sel.form);
      if (samePage(url)) load(url.href, true, false); else location.href = url.href;
    });
    b.on(root, 'submit', function (e) {
      var form = e.target;
      if (!form.matches('[data-hvp-col-sort]')) return;
      var url = sortUrl(form);
      if (!samePage(url)) return;
      e.preventDefault();
      load(url.href, true, false);
    });

    // tag the current entry so Back to it re-renders the grid too
    try { history.replaceState(Object.assign({}, history.state || {}, { hvpCol: sid }), ''); } catch (e) { /* noop */ }
    b.on(window, 'popstate', function (e) {
      if (e.state && e.state.hvpCol === sid) load(location.href, false, false);
    });
    b.add(function () { if (ctrl) ctrl.abort(); });

    return { destroy: b.run };
  };

  /* Search: plain form + links, nothing to wire beyond reveals */
  modules.search = function () { return {}; };

  /* ================================================================
     REGISTRY + THEME EDITOR
     ================================================================ */
  function initIn(scope) {
    var roots = $$('[data-hvp]', scope);
    if (scope.nodeType === 1 && scope.hasAttribute('data-hvp')) roots.unshift(scope);
    roots.forEach(function (root) {
      if (root.__hvp) return;
      var mod = modules[root.getAttribute('data-hvp')];
      try { root.__hvp = (mod && mod(root)) || {}; } catch (err) { if (window.console) console.error('[hvp]', root.getAttribute('data-hvp'), err); root.__hvp = {}; }
      setupReveals(root);
    });
  }
  function destroyIn(scope) {
    var roots = $$('[data-hvp]', scope);
    if (scope.nodeType === 1 && scope.hasAttribute('data-hvp')) roots.unshift(scope);
    roots.forEach(function (root) {
      if (root.__hvp && root.__hvp.destroy) { try { root.__hvp.destroy(); } catch (e) { /* noop */ } }
      forgetReveals(root);
      delete root.__hvp;
    });
  }

  function boot() { initIn(d); }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot); else boot();

  d.addEventListener('shopify:section:load', function (e) { initIn(e.target); });
  d.addEventListener('shopify:section:unload', function (e) { destroyIn(e.target); });
  d.addEventListener('shopify:block:select', function (e) {
    var host = e.target.closest && e.target.closest('[data-hvp]');
    if (host && host.__hvp && host.__hvp.select) host.__hvp.select(e.target, e);
  });

  window.HVP = { loaded: true, init: initIn, destroy: destroyIn };
})();
